import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Bell,
    BellRing,
    BellOff,
    Mail,
    Clock3,
    CalendarClock,
    AlertTriangle,
    ShieldAlert,
    CheckCircle2,
    Save,
    Loader2,
    Info,
    Wrench,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const PERIOD_OPTIONS = [
    { value: 1, label: 'Каждый день' },
    { value: 7, label: 'Раз в неделю' },
    { value: 14, label: 'Раз в 2 недели' },
    { value: 30, label: 'Раз в месяц' },
    { value: 90, label: 'Раз в квартал' },
];

const THRESHOLD_OPTIONS = [
    { value: 1, label: 'за 1 месяц' },
    { value: 3, label: 'за 3 месяца' },
    { value: 6, label: 'за 6 месяцев' },
    { value: 9, label: 'за 9 месяцев' },
    { value: 12, label: 'за 12 месяцев' },
];

const EVENT_META = {
    verificationDue: { icon: CalendarClock, color: 'text-amber-400', bg: 'bg-amber-500/15 border-amber-500/30', hasThreshold: true },
    ns: { icon: ShieldAlert, color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/30', hasThreshold: false },
    commissioningExpiry: { icon: Wrench, color: 'text-blue-400', bg: 'bg-blue-500/15 border-blue-500/30', hasThreshold: true },
};
const EVENT_FALLBACK = { icon: Bell, color: 'text-slate-400', bg: 'bg-slate-600/15 border-slate-600/30', hasThreshold: false };

const Toggle = ({ checked, onChange, disabled = false }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition flex-shrink-0
            ${checked ? 'bg-blue-600' : 'bg-slate-600'}
            ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        `}
    >
        <span
            className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'
                }`}
        />
    </button>
);

const NotificationsTab = ({ userInfo }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [form, setForm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await axios.get(`${API_URL}/api/notifications/settings`, authHeaders());
            setForm({
                email: data.email || userInfo?.email || '',
                isEnabled: !!data.isEnabled,
                events: (data.events || []).map((e) => ({ ...e })),
            });
        } catch (err) {
            console.error('Ошибка загрузки настроек уведомлений:', err);
            setError(err.response?.data?.message || 'Не удалось загрузить настройки уведомлений');
        } finally {
            setLoading(false);
        }
    }, [userInfo]);

    useEffect(() => { load(); }, [load]);

    const setField = (field, value) => {
        setSaved(false);
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateEvent = (type, patch) => {
        setSaved(false);
        setForm((prev) => ({
            ...prev,
            events: prev.events.map((e) => (e.type === type ? { ...e, ...patch } : e)),
        }));
    };

    const handleSave = async () => {
        setError('');
        if (form.isEnabled && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            setError('Укажите корректный e-mail для получения уведомлений');
            return;
        }

        // Валидация per-event настроек для включённых событий
        for (const ev of form.events) {
            if (!ev.isEnabled || !ev.available) continue;
            if (ev.periodDays < 1 || ev.periodDays > 365) {
                setError(`Некорректная периодичность для события «${ev.name}»`);
                return;
            }
        }

        setSaving(true);
        try {
            await axios.put(
                `${API_URL}/api/notifications/settings`,
                {
                    email: form.email.trim(),
                    isEnabled: form.isEnabled,
                    events: form.events.map((e) => ({
                        type: e.type,
                        isEnabled: e.isEnabled,
                        periodDays: e.periodDays,
                        verificationThresholdMonths: e.verificationThresholdMonths ?? null,
                    })),
                },
                authHeaders()
            );
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
            await load();
        } catch (err) {
            setError(err.response?.data?.message || 'Не удалось сохранить настройки');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                <div className="text-slate-400 text-xs">Загрузка настроек уведомлений…</div>
            </div>
        );
    }

    if (!form) {
        return (
            <div className="bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error || 'Не удалось загрузить настройки'}
                </span>
                <button
                    onClick={load}
                    className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-xs font-medium transition whitespace-nowrap"
                >
                    Повторить
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-slide-down-fade">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Bell className="w-5 h-5 text-blue-400" />
                        Уведомления
                    </h2>
                    <p className="text-slate-400 text-xs mt-0.5">
                        У каждого типа события свои настройки периодичности и условий
                    </p>
                </div>
            </div>

            {error && (
                <div className="px-4 py-3 bg-red-600/20 border border-red-600/40 rounded-lg text-red-300 text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> {error}
                    </span>
                    <button onClick={() => setError('')}>✕</button>
                </div>
            )}

            {saved && (
                <div className="px-4 py-3 bg-green-600/20 border border-green-600/40 rounded-lg text-green-300 text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Настройки сохранены
                </div>
            )}

            {/* ===== Главный тумблер + e-mail ===== */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${form.isEnabled
                                    ? 'bg-blue-500/15 border-blue-500/30'
                                    : 'bg-slate-700/50 border-slate-600/50'
                                }`}
                        >
                            {form.isEnabled ? (
                                <BellRing className="w-5 h-5 text-blue-400" />
                            ) : (
                                <BellOff className="w-5 h-5 text-slate-500" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-white font-semibold text-sm">
                                E-mail-уведомления
                            </h3>
                            <p className="text-slate-400 text-xs mt-0.5">
                                Главный переключатель рассылки
                            </p>
                        </div>
                    </div>
                    <Toggle checked={form.isEnabled} onChange={(v) => setField('isEnabled', v)} />
                </div>

                <div className={`transition ${form.isEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
                    <label className="block text-xs font-medium text-slate-400 mb-1">
                        E-mail для уведомлений
                    </label>
                    <div className="relative">
                        <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setField('email', e.target.value)}
                            placeholder="example@mail.ru"
                            className="w-full pl-9 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                        />
                    </div>
                    <p className="text-slate-500 text-[11px] mt-1.5 flex items-start gap-1">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        Отписаться можно в любой момент: по ссылке в письме или выключив этот переключатель
                    </p>
                </div>
            </div>

            {/* ===== Типы уведомлений с per-event настройками ===== */}
            <div
                className={`space-y-3 transition ${form.isEnabled ? '' : 'opacity-50 pointer-events-none'}`}
            >
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                    <Bell className="w-4 h-4 text-slate-400" />
                    Типы уведомлений
                </h3>

                {form.events.map((event) => {
                    const meta = EVENT_META[event.key] || EVENT_FALLBACK;
                    const Icon = meta.icon;
                    const showSettings = event.isEnabled && event.available;

                    // Защита от нестандартных значений
                    const periodOpts = PERIOD_OPTIONS.some((o) => o.value === event.periodDays)
                        ? PERIOD_OPTIONS
                        : [...PERIOD_OPTIONS, { value: event.periodDays, label: `Раз в ${event.periodDays} дн.` }];

                    const thresholdOpts = THRESHOLD_OPTIONS.some((o) => o.value === event.verificationThresholdMonths)
                        ? THRESHOLD_OPTIONS
                        : event.verificationThresholdMonths
                            ? [...THRESHOLD_OPTIONS, { value: event.verificationThresholdMonths, label: `за ${event.verificationThresholdMonths} мес.` }]
                            : THRESHOLD_OPTIONS;

                    return (
                        <div
                            key={event.type}
                            className={`rounded-xl border overflow-hidden transition ${event.isEnabled && event.available
                                    ? 'border-blue-500/30 bg-slate-800'
                                    : 'border-slate-700 bg-slate-800/50'
                                }`}
                        >
                            {/* Header */}
                            <div className="flex items-center gap-3 p-4">
                                <div className={`w-10 h-10 rounded-lg border flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                                    <Icon className={`w-5 h-5 ${meta.color}`} />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-white text-sm font-medium">
                                            {event.name}
                                        </span>
                                        {!event.available && (
                                            <span className="px-1.5 py-0.5 rounded bg-slate-600/30 border border-slate-600/50 text-slate-400 text-[10px] font-medium whitespace-nowrap">
                                                Скоро
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-slate-500 text-xs mt-0.5">
                                        {event.description}
                                    </p>
                                </div>

                                {event.available ? (
                                    <Toggle
                                        checked={event.isEnabled}
                                        onChange={(v) => updateEvent(event.type, { isEnabled: v })}
                                    />
                                ) : (
                                    <Toggle checked={false} onChange={() => { }} disabled />
                                )}
                            </div>

                            {/* Per-event настройки */}
                            {showSettings && (
                                <div className="px-4 pb-4 pt-1 border-t border-slate-700/60 space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {/* Периодичность — для всех типов */}
                                        <div>
                                            <label className="block text-[11px] font-medium text-slate-400 mb-1 flex items-center gap-1">
                                                <Clock3 className="w-3 h-3" />
                                                Периодичность отправки
                                            </label>
                                            <select
                                                value={event.periodDays}
                                                onChange={(e) =>
                                                    updateEvent(event.type, { periodDays: Number(e.target.value) })
                                                }
                                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                {periodOpts.map((o) => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Порог предупреждения — только для типов с hasThreshold */}
                                        {meta.hasThreshold && (
                                            <div>
                                                <label className="block text-[11px] font-medium text-slate-400 mb-1 flex items-center gap-1">
                                                    <CalendarClock className="w-3 h-3" />
                                                    Предупреждать
                                                </label>
                                                <select
                                                    value={event.verificationThresholdMonths || 6}
                                                    onChange={(e) =>
                                                        updateEvent(event.type, {
                                                            verificationThresholdMonths: Number(e.target.value),
                                                        })
                                                    }
                                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    {thresholdOpts.map((o) => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>

                                    {event.lastSentAt && (
                                        <p className="text-slate-500 text-[11px] flex items-center gap-1.5 pt-1">
                                            <Clock3 className="w-3 h-3 flex-shrink-0" />
                                            Последнее письмо по этому событию:{' '}
                                            <span className="text-slate-300">
                                                {new Date(event.lastSentAt).toLocaleDateString('ru-RU')}
                                            </span>
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* ===== Кнопка сохранения ===== */}
            <div className="flex justify-end pt-2">
                <button
                    onClick={handleSave}
                    disabled={saving || !form.isEnabled}
                    className="
                        px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-60
                        text-white rounded-lg text-sm font-medium transition
                        flex items-center gap-2 shadow-lg shadow-blue-600/20
                    "
                >
                    {saving ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Сохранение…
                        </>
                    ) : (
                        <>
                            <Save className="w-4 h-4" />
                            Сохранить настройки
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default NotificationsTab;